import React, { useEffect, useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";

/**
 * Async typeahead combobox.
 * Props:
 *   placeholder: string
 *   fetcher: (query) => Promise<{ id, name, sub }[]>  // sub = secondary line
 *   onSelect: (item) => void
 *   minChars: number (default 1)
 *   icon: ReactNode
 *   debounceMs: number (default 200)
 */
export function AsyncCombobox({
  placeholder = "Search…",
  fetcher,
  onSelect,
  minChars = 1,
  icon = null,
  debounceMs = 200,
  triggerLabel,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (query.length < minChars) {
      // pre-load with empty query (top items)
      setLoading(true);
      fetcher("").then((r) => {
        setItems(r);
        setLoading(false);
      });
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      fetcher(query).then((r) => {
        setItems(r);
        setLoading(false);
      });
    }, debounceMs);
    return () => clearTimeout(t);
  }, [query, open, fetcher, minChars, debounceMs]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="justify-between gap-2 min-w-[200px]"
          data-testid={testId}
        >
          {icon}
          <span className="truncate text-muted-foreground">{triggerLabel || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={query}
            onValueChange={setQuery}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <CommandList>
            {loading && (
              <div className="py-3 px-3 text-xs text-muted-foreground">Searching…</div>
            )}
            {!loading && items.length === 0 && (
              <CommandEmpty>No matches.</CommandEmpty>
            )}
            {!loading && items.length > 0 && (
              <CommandGroup>
                {items.map((it) => (
                  <CommandItem
                    key={it.id}
                    value={it.id}
                    onSelect={() => {
                      setOpen(false);
                      setQuery("");
                      onSelect(it);
                    }}
                    data-testid={testId ? `${testId}-item-${it.id}` : undefined}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="truncate font-medium">{it.name}</span>
                      {it.sub && (
                        <span className="truncate text-xs text-muted-foreground">{it.sub}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
